import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery, Theme, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { intermitenceSelector, storySelector } from '@/store/selectors';
import {
  createStories,
  getUploadSignedUrl,
  createStoryViewG,
  updateSubscriptionStatus,
  openSubscriptionPopup,
  openModal,
} from '@/store/actions';
import { FormStories, FinishCreate } from './components';
import { useRouter } from 'next/router';
import { formsByCategory } from './forms';

import { finalPayload, transformPayload } from '@/utils/transformPayload';
import { calculateFileSize, checkRoleAndPermission, FetchFileService } from '@/utils';
import { UseFirstRender } from '@/hooks';
import { RefreshUserData } from '@/utils/fetchUserData';
const Custom_Story = {
  title: 'Custom Story',
  text: 'custom_story',
  value: 6,
  url: 'custom-story',
  bgcolor: '#FED9D1',
  color: '#B97B6E',
  content: 'custom_story',
};
const templates = [
  {
    title: 'Life Story',
    text: 'life_story',
    value: 0,
    url: 'life-story',
    content: 'life_story_content',
    bgcolor: '#FED9D1',
    color: '#B97B6E',
    icon: '/icons/lifeStory.svg',
    position: 'center',
  },
  {
    title: 'Wedding Story',
    text: 'wedding_story',
    value: 1,
    url: 'classmates-story',
    content: 'classmates_story_content',
    bgcolor: '#0072CE',
    color: '#D8E8F6',
    icon: '/icons/wedding.svg',
    position: 'right',
  },
  {
    title: 'Retirement Story',
    text: 'retirement_story',
    value: 2,
    url: 'teammates-story',
    content: 'teammates_story_content',
    bgcolor: '#89D3FF',
    color: '#0072CE',
    icon: '/icons/retirement.svg',
    position: 'left',
  },
  {
    title: 'Classmates Story',
    text: 'classmates_story',
    value: 3,
    bgcolor: '#F1E0FF',
    color: '#8C58B8',
    icon: '/icons/classmate.svg',
    position: 'center',
  },
  {
    title: 'Teammates Story',
    text: 'teammates_story',
    value: 4,
    bgcolor: '#FED9D1',
    color: '#B97B6E',
    icon: '/icons/teammate.svg',
    position: 'center',
  },
  {
    title: 'Tribute Story',
    text: 'tribute_story',
    value: 5,
    bgcolor: '#0072CE',
    color: '#D8E8F6',
    icon: '/icons/tribute.svg',
    position: 'center',
  },
];

type selectedPromptsState = {
  [key: string]: boolean;
};

const ShowScreen: FC<any> = ({
  createStep,
  setHandleForm,
  handleForm,
  setArrayRun,
  setSelectedPrompts,
  selectedPrompts,
  submit,
  values,
  setValues,
  selectedForm,
  setSelectedForm,
  currentFormConfig,
  setActualFormNumber,
  actualFormNumber,
  submitAction,
  handleCreateStories,
  creating,
}) => {
  switch (createStep) {
    case 1:
      return (
        <FormStories
          {...{
            submit,
            values,
            setValues,
            selectedForm,
            setSelectedForm,
            currentFormConfig,
            setActualFormNumber,
            actualFormNumber,
            createStep,
            setArrayRun,
            submitAction,
            handleForm,
            setHandleForm,
            handleCreateStories,
            creating,
          }}
        />
      );

    case 2:
      return <FinishCreate {...{ handleCreateStories, creating }} />;

    default:
      return (
        <FormStories
          {...{
            submit,
            values,
            setValues,
            selectedForm,
            setSelectedForm,
            currentFormConfig,
            setActualFormNumber,
            actualFormNumber,
            createStep,
            setArrayRun,
            submitAction,
            handleForm,
            setHandleForm,
            handleCreateStories,
            creating,
          }}
        />
      );
  }
};

export const CreateStory = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const createData = useSelector(storySelector);
  const intermitenceData = useSelector(intermitenceSelector);
  const { createStep, storySection, prev_stories, prompts } = createData;
  const user = useSelector((state: any) => state.auth.user);

  const [arrayRun, setArrayRun] = useState(0);
  const [handleForm, setHandleForm] = useState({ execute: false, form: '' });
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const currentFormConfig = useMemo(() => formsByCategory?.[storySection], [storySection]);
  const [actualFormNumber, setActualFormNumber] = useState(0);
  const [values, setValues] = useState<any>({});
  const [previous_stories, setPrevious_stories] = useState<any>({});
  const [selectedPrompts, setSelectedPrompts] = useState<selectedPromptsState>({});
  const [creating, setCreating] = useState(false);
  const submit = useRef<any>(null);

  const submitAction = () => {
    if (Array.isArray(submit.current)) {
      submit.current.forEach((item: any) => {
        if (Array.isArray(item)) {
          item.forEach((item: any) => {
            item?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          });
        } else {
          item?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
      return;
    }
    if (submit?.current) {
      submit?.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  UseFirstRender(() => {
    if (user?.id) {
      dispatch(createStoryViewG(user.id));
    }
  }, [dispatch]);

  const processFile = async (prev_stories: any, prompts: {}) => {
    const file = values?.story_title_image?.cover_image;
    const StoryType = localStorage.getItem('storyType') ? localStorage.getItem('storyType') : storySection;

    const fileSizeBytes = file ? calculateFileSize(file) : 0;

    if (file && fileSizeBytes > 0) {
      dispatch(
        getUploadSignedUrl(
          { file: `stories/${prev_stories?.title}/${prev_stories?.name_image}`, type: prev_stories?.type_image },
          async (res: any) => {
            try {
              const response = await FetchFileService(
                res?.value?.url?.uploadUrl,
                'PUT',
                file,
                prev_stories?.type_image,
              );

              if (response?.ok) {
                const valuesFinal = finalPayload({ ...prev_stories, fileSize: fileSizeBytes }, StoryType);
                dispatch(updateSubscriptionStatus({ userId: user?.id }));

                const res = await RefreshUserData(user?.token, user?.id);
                const userData = res?.result;
                const subsperm = checkRoleAndPermission(
                  userData?.roles,
                  'Subscriber_Individual',
                  'CLIENT_STORY_CREATE',
                  user?.id,
                );

                if (!subsperm) {
                  setCreating(false);
                  localStorage.setItem('router_history', `/app/home`);
                  localStorage.setItem('pendingStory', JSON.stringify(valuesFinal));
                  dispatch(openSubscriptionPopup());
                  return;
                }

                const result = dispatch(createStories({ valuesFinal, router, flag: true }));
                return result;
              }
            } catch (error) {
              console.error('Error uploading file:', error);
            }
          },
        ),
      );
    } else {
      const valuesFinal = finalPayload({ ...prev_stories, fileSize: fileSizeBytes }, storySection);
      dispatch(updateSubscriptionStatus({ userId: user?.id }));

      const res = await RefreshUserData(user?.token, user?.id);
      const userData = res?.result;
      const subsperm = checkRoleAndPermission(
        userData?.roles,
        'Subscriber_Individual',
        'CLIENT_STORY_CREATE',
        user?.id,
      );

      if (!subsperm) {
        setCreating(false);
        localStorage.setItem('router_history', `/app/home`);
        localStorage.setItem('pendingStory', JSON.stringify(valuesFinal));
        dispatch(openSubscriptionPopup());
        return;
      }

      const result = dispatch(createStories({ valuesFinal, router, flag: true }));
      setCreating(false);
      return result;
    }
  };

  const handleCreateStories = async () => {
    setCreating(true);

    const imageFile = values?.story_title_image?.cover_image || values?.story_title?.cover_image || null;
    const StoryType = localStorage.getItem('storyType') ? localStorage.getItem('storyType') : storySection;
    const valuesCopy = transformPayload(
      values,
      StoryType,
      user.id,
      user.email,
      imageFile?.name || '',
      imageFile?.type || '',
    );

    setPrevious_stories(valuesCopy);

    dispatch(updateSubscriptionStatus({ userId: user?.id }));

    const res = await RefreshUserData(user?.token, user?.id);
    const userData = res?.result;

    const fileSizeBytes = imageFile ? calculateFileSize(imageFile) : 0;

    const usedStorage = userData?.usedStorage || 0;
    const totalStorage = userData?.totalStorage || 0;

    if (usedStorage + fileSizeBytes > totalStorage) {
      setCreating(false);
      dispatch(openModal({ content: 'There is not enough storage in your account.' }));
      return;
    }

    if (!imageFile) {
      const valuesFinal = finalPayload({ ...valuesCopy, fileSize: 0 }, storySection);
      const subsperm = checkRoleAndPermission(
        userData?.roles,
        'Subscriber_Individual',
        'CLIENT_STORY_CREATE',
        user?.id,
      );

      if (!subsperm) {
        setCreating(false);
        localStorage.setItem('router_history', `/app/home`);
        localStorage.setItem('pendingStory', JSON.stringify(valuesFinal));
        dispatch(openSubscriptionPopup());
        return;
      }

      dispatch(createStories({ valuesFinal, router, flag: true }));
      setCreating(false);
      return;
    }

    processFile(valuesCopy, prompts);
  };

  useEffect(() => {
    if (createStep == 0) setValues({});
  }, [createStep]);
  const allowPadding = () => {
    if (router.pathname.includes('/app/story/create') && isMobile) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <Box
      display={'flex'}
      width={'100%'}
      justifyContent={'flex-start'}
      flexDirection={'column'}
      alignItems={isMobile ? 'flex-start' : 'center'}>
      <Box
        position={'relative'}
        width={'100%'}
        zIndex={0}
        paddingTop={allowPadding() ? intermitenceData.separation : ''}
        height={'auto'}>
        <ShowScreen
          {...{
            handleForm,
            setHandleForm,
            createStep,
            setArrayRun,
            setSelectedPrompts,
            selectedPrompts,
            submit,
            values,
            setValues,
            selectedForm,
            setSelectedForm,
            currentFormConfig,
            setActualFormNumber,
            actualFormNumber,
            submitAction,
            handleCreateStories,
            creating,
          }}
        />
      </Box>
    </Box>
  );
};
